import { useEffect, useRef, useState, type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldAlert, ShieldCheck, ShieldOff } from "lucide-react";
import { Button, EmptyState, Wordmark } from "@playbookai/ui";
import { PRODUCT_NAME } from "@playbookai/types";
import { readOauthResult } from "@playbookai/api-clients";
import { SETTINGS_CONNECTIONS_ROUTE } from "../../routes.js";
import { completionMessage } from "./completion-message.js";
import { publishIntegrationOauthCompletion } from "./completion-channel.js";

/**
 * PB-1592 — where the OAuth round trip ends, and the second PUBLIC route in
 * this app.
 *
 * IT IS PUBLIC ON PURPOSE, and that is the whole reason it exists. The provider
 * redirects to the backend callback, which 303s the browser here — a plain
 * navigation in a tab the console opened, carrying no bearer token and no
 * guarantee the session is still live, because a slow authorization can outlast
 * an access token. Landing that tab on the authenticated
 * `/settings/connections` therefore risked bouncing a completed authorization
 * into a fresh sign-in, in a tab the user never asked to sign in from. Here it
 * lands on a page that needs nothing.
 *
 * WHAT IT KNOWS IS ONLY WHAT IS IN THE URL: an outcome, and either a connection
 * id it never displays or a sanitised error code. Never the `state`, never the
 * `code`, never a token — the backend deliberately does not reflect those, and
 * this page must not start needing them. It reads no API and holds no session,
 * so there is nothing here to leak into browser history or a referrer.
 *
 * It reports the outcome and broadcasts a contentless signal
 * ([[publishIntegrationOauthCompletion]]). The console that started the flow
 * refetches its own authenticated APIs on that signal and believes THOSE — this
 * page's claim about what happened is unauthenticated, so it is a prompt, not
 * evidence.
 */

/**
 * How long a finished, non-error outcome stays on screen before the tab closes
 * itself.
 *
 * Long enough to read, short enough not to feel stuck. It governs TWO outcomes:
 * a plain success, and — since PB-5318 — a cancellation. Both are over and leave
 * the user nothing to act on, and for a cancellation closing is what reveals the
 * tab they started in.
 *
 * Two outcomes deliberately stay put. A failure never auto-closes: the code on
 * screen is the only copy the user has, and closing it for them destroys the one
 * thing they could quote to an administrator. Nor does a duplicate-account
 * success, whose explanation is the whole point of it.
 */
export const OAUTH_COMPLETION_AUTO_CLOSE_MS = 2_500;

export function IntegrationOauthComplete(): ReactElement {
  const [searchParams] = useSearchParams();
  // Frozen at mount. The banner must not change under the user because a
  // parameter was stripped or a back button replayed a different query.
  const [result] = useState(() => readOauthResult(searchParams));
  const message = completionMessage(result);
  const announced = useRef(false);
  const [closeRefused, setCloseRefused] = useState(false);

  useEffect(() => {
    // A round trip that did not happen announces nothing: somebody opened this
    // URL directly, and telling the console to refetch would be a lie about a
    // flow it never started.
    if (result === null || announced.current) return;
    // React 18 StrictMode mounts effects twice in development. A second
    // broadcast is harmless — the listener refetches, and refetching twice is
    // the same answer — but the guard keeps the intent honest: one round trip,
    // one signal.
    announced.current = true;
    publishIntegrationOauthCompletion();
  }, [result]);

  useEffect(() => {
    // Two outcomes close themselves: a plain success, and a cancellation
    // (PB-5318). Both are finished and neither leaves the user anything to
    // read off the screen — and for a cancellation, closing is precisely what
    // returns them to the tab they started from, which is already showing the
    // same news. A duplicate-account success stays, because its explanation is
    // the point of it, and a failure stays because its code is the only copy
    // the user has.
    const closesItself =
      result?.outcome === "cancelled" ||
      (result?.outcome === "connected" && !result.alreadyConnected);
    if (!closesItself) return;
    const timer = setTimeout(() => {
      closeTab();
      // Still rendered a moment later means the browser refused, so reveal the
      // manual way out. Harmless when it worked: the tab is gone by then.
      setCloseRefused(true);
    }, OAUTH_COMPLETION_AUTO_CLOSE_MS);
    // Cleared on unmount so a StrictMode remount cannot leave two timers, and
    // so navigating away by clicking the link below does not close the tab the
    // user just chose to keep.
    return () => clearTimeout(timer);
  }, [result?.outcome, result?.alreadyConnected]);

  /**
   * `window.close()` succeeds for a script-opened tab, which this is — but a
   * browser is entitled to refuse, and an extension or a hardened
   * configuration will. A refusal is silent, so the button reveals the way out
   * by hand rather than looking dead.
   */
  const closeNow = (): void => {
    closeTab();
    setCloseRefused(true);
  };

  const connected = message.tone === "success";
  const cancelled = message.tone === "cancelled";

  /* PB-5318 — a cancellation offers NO destination unless closing the tab has
     actually failed, and then only a neutral one. "Return to connection
     settings" is a promise this page cannot keep: it holds nothing about where
     the flow began, so it sent an administrator who cancelled from the Admin
     Console to personal settings instead. Closing is the real way back — it
     reveals the tab they started in — and the app root is the one destination
     that is honest from either surface.

     Every other branch keeps the original link: if the browser refuses to
     close the tab there is otherwise no way forward. The link is an ordinary
     in-app navigation, so it goes through the authenticated guard exactly like
     typing the URL would. Kept as today's underlined text-pb-text router link:
     the design-system Link paints cyan (or secondary) with a 2px underline
     offset, a look change no PR 3 row approves. */
  const outcomeLink = cancelled ? (
    closeRefused ? (
      <Link
        className="text-body-small font-medium text-pb-text underline underline-offset-4"
        to="/"
      >
        Go to {PRODUCT_NAME}
      </Link>
    ) : undefined
  ) : (
    <Link
      className="text-body-small font-medium text-pb-text underline underline-offset-4"
      to={SETTINGS_CONNECTIONS_ROUTE}
    >
      Return to connection settings
    </Link>
  );

  return (
    <main className="pb-motion-content-enter flex min-h-screen flex-col items-center justify-center gap-6 bg-pb-page px-6">
      {/* The product mark above the card, outside its live region, so the
          outcome is announced without the product name in front of it. */}
      <div className="text-pb-text">
        <Wordmark label={PRODUCT_NAME} />
      </div>
      {/* The page-level outcome screen: EmptyState lg (OK-33) in today's
          frame. The medallion carries the outcome colour — green for success,
          red for failure, and neutral for a cancellation, which is neither —
          so the icon and the role tell one story. The h1 stays this page's
          own, inside the EmptyState title. The frame is kept hand-built: Card
          takes an 80% border in place of today's strong one and its 1px inset
          top hairline, a look change no PR 3 row approves. */}
      <section
        // A failure is announced assertively; a success only politely, and a
        // cancellation politely too — the user performed it, so interrupting
        // them to announce it back would be the wrong register (PB-5318).
        role={connected || cancelled ? "status" : "alert"}
        aria-labelledby="integration-oauth-complete-title"
        className="w-full max-w-md rounded-xl border border-pb-border-strong bg-pb-card"
      >
        <EmptyState
          {...(connected
            ? { tone: "healthy" as const }
            : cancelled
              ? {}
              : { variant: "error" as const })}
          size="lg"
          icon={
            connected ? (
              <ShieldCheck className="size-6" aria-hidden="true" />
            ) : cancelled ? (
              <ShieldOff className="size-6" aria-hidden="true" />
            ) : (
              <ShieldAlert className="size-6" aria-hidden="true" />
            )
          }
          title={<h1 id="integration-oauth-complete-title">{message.title}</h1>}
          description={
            <>
              {message.detail}
              {closeRefused ? (
                <span className="mt-2 block text-caption text-pb-text-muted">
                  If this tab is still open, your browser blocked closing it —
                  close it yourself, or use the link above.
                </span>
              ) : null}
            </>
          }
          action={
            connected || cancelled ? (
              <Button variant="secondary" onClick={closeNow}>
                Close
              </Button>
            ) : undefined
          }
          secondaryAction={outcomeLink}
        />
      </section>
    </main>
  );
}

function closeTab(): void {
  try {
    window.close();
  } catch {
    // Refusing to close is a browser policy decision, not an error this page
    // can act on. The manual affordances above are already on screen.
  }
}
